import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { SalesShell } from "@/components/crm/SalesShell";
import { EmptyState, SelectField, Stat } from "@/components/crm/parts";
import { useAccounts } from "@/lib/crm/useCrm";
import {
  customerResolver,
  monthKey,
  monthLabel,
  useCustomerWork,
  type AccountRef,
} from "@/lib/crm/customer-work";
import { round1 } from "@/lib/hub";

export const Route = createFileRoute("/_authenticated/sales/customer-hours")({
  head: () => ({
    meta: [
      { title: "Hours per customer — MyPriorities" },
      {
        name: "description",
        content: "Month-by-month view of how many hours your team spends on each customer account.",
      },
      { property: "og:title", content: "Hours per customer — MyPriorities" },
      {
        property: "og:description",
        content: "See where your delivery time actually goes, customer by customer, month by month.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: CustomerHoursPage,
});

function CustomerHoursPage() {
  return (
    <SalesShell
      section="customers"
      title="Hours per customer"
      subtitle="Logged hours from completed work, rolled up by customer and month."
    >
      {({ orgId }) => <CustomerHours orgId={orgId} />}
    </SalesShell>
  );
}

const RANGES = [
  { value: "6", label: "Last 6 months" },
  { value: "12", label: "Last 12 months" },
  { value: "24", label: "Last 24 months" },
] as const;

function lastMonths(count: number) {
  const now = new Date();
  const keys: string[] = [];
  for (let i = count - 1; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    keys.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return keys;
}

function CustomerHours({ orgId }: { orgId: string }) {
  const accounts = useAccounts(orgId);
  const work = useCustomerWork(orgId);
  const [range, setRange] = useState<string>("6");

  const refs = useMemo<AccountRef[]>(
    () => (accounts.data ?? []).map((a) => ({ id: a.id, name: a.name })),
    [accounts.data],
  );
  const resolver = useMemo(() => customerResolver(refs, work.links), [refs, work.links]);
  const months = useMemo(() => lastMonths(Number(range)), [range]);

  const rows = useMemo(() => {
    const window = new Set(months);
    const map = new Map<string, { account: AccountRef; byMonth: Map<string, number>; total: number }>();

    for (const done of work.completions) {
      const key = monthKey(done.completed_on);
      if (!window.has(key)) continue;
      const hours = Number(done.hours || 0);
      if (!hours) continue;
      for (const account of resolver.accountsFor(done)) {
        const row =
          map.get(account.id) ?? { account, byMonth: new Map<string, number>(), total: 0 };
        row.byMonth.set(key, round1((row.byMonth.get(key) ?? 0) + hours));
        row.total = round1(row.total + hours);
        map.set(account.id, row);
      }
    }

    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [resolver, work.completions, months]);

  const grandTotal = round1(rows.reduce((sum, row) => sum + row.total, 0));
  const busiest = rows[0];
  const perMonth = months.map((key) => ({
    key,
    hours: round1(rows.reduce((sum, row) => sum + (row.byMonth.get(key) ?? 0), 0)),
  }));
  const peak = Math.max(1, ...perMonth.map((m) => m.hours));

  if (work.isLoading || accounts.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading customer hours…</p>;
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <Stat label="Customer hours" value={`${grandTotal}h`} hint={RANGES.find((r) => r.value === range)?.label ?? ""} />
        <Stat label="Customers served" value={String(rows.length)} />
        <Stat
          label="Busiest customer"
          value={busiest ? busiest.account.name : "—"}
          {...(busiest ? { hint: `${busiest.total}h` } : {})}
        />
      </div>

      <div className="max-w-xs">
        <SelectField label="Period" value={range} onChange={setRange} options={RANGES} />
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title="No customer hours logged yet"
          body="Complete work that is linked or client-tagged to an account and the hours will roll up here."
        />
      ) : (
        <>
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="label-caps">Total hours by month</p>
            <div className="mt-3 flex items-end gap-2">
              {perMonth.map((m) => (
                <div key={m.key} className="flex flex-1 flex-col items-center gap-1">
                  <span className="text-[11px] tabular-nums text-muted-foreground">{m.hours}</span>
                  <div
                    className="w-full rounded-t bg-primary/70"
                    style={{ height: `${Math.round((m.hours / peak) * 96) + 2}px` }}
                  />
                  <span className="text-[10px] text-muted-foreground">{monthLabel(m.key)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">Customer</th>
                  {months.map((key) => (
                    <th key={key} className="px-3 py-2 text-right font-medium">
                      {monthLabel(key)}
                    </th>
                  ))}
                  <th className="px-3 py-2 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.account.id} className="border-t border-border">
                    <td className="px-3 py-2">{row.account.name}</td>
                    {months.map((key) => (
                      <td key={key} className="px-3 py-2 text-right tabular-nums">
                        {row.byMonth.get(key) ?? "—"}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right font-medium tabular-nums">{row.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
